import { useEffect, useState } from 'react';
import { getAiConsent, setAiConsent } from '../../lib/aiConsent';

export function AiRecommendationsAccountControl() {
  const [loaded, setLoaded] = useState(false);
  const [granted, setGranted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const consent = await getAiConsent();
        if (cancelled) return;
        setGranted(consent.granted);
        setLoaded(true);
      } catch {
        if (!cancelled) {
          setError("Couldn't load this setting. Check your connection and try again.");
          setLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = async (next: boolean) => {
    const previous = granted;
    setSaving(true);
    setError(null);
    try {
      const saved = await setAiConsent(next);
      setGranted(saved.granted);
    } catch {
      setGranted(previous);
      setError("Couldn't save your choice. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="cine-card animate-rise delay-125 mb-6 rounded-2xl p-6">
      <h2 className="mb-1 text-lg font-semibold text-foreground">AI recommendations</h2>
      <p id="ai-rec-help" className="mb-4 text-sm leading-relaxed text-muted-foreground">
        When on, MovieMe can send your recommendation choices and Watched titles to OpenAI to
        generate picks and avoid repeats. Your email and Watchlist are not sent.
      </p>

      {!loaded ? (
        <p className="text-sm text-muted-foreground">Loading permission…</p>
      ) : (
        <label className="flex min-h-[44px] cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            role="switch"
            className="h-5 w-5 accent-primary"
            checked={granted}
            disabled={saving}
            aria-describedby="ai-rec-help ai-rec-state"
            onChange={(event) => {
              void handleToggle(event.target.checked);
            }}
          />
          <span className="text-sm font-medium text-foreground">Use OpenAI for recommendations</span>
        </label>
      )}

      {loaded && !granted ? (
        <p id="ai-rec-state" className="mt-3 text-sm text-muted-foreground">
          New AI recommendations stay unavailable until you turn this on.
        </p>
      ) : (
        <p id="ai-rec-state" className="sr-only">
          AI recommendations are on.
        </p>
      )}

      {error ? (
        <p className="mt-3 text-sm text-red-300" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
